import { useMemo, useState, useCallback, useEffect } from 'react';
import { Colors } from '@mercator-gcp/grout';
import { Marker, Polygon } from '@react-google-maps/api';
import { isEqual } from 'lodash';
import { Map, MapProps } from 'components/elements/Map';
import { useParcelPolygon } from 'hooks/projectMap/useParcelPolygons';
import { useStreetViewVisibility } from 'hooks/projectMap/useStreetViewVisibility';
import {
  MapTypeId,
  useUserPreferredMapType,
} from 'hooks/projectMap/useUserPreferredMapType';
import { useDebouncedSetCenterQuery } from 'hooks/useProjectMap';
import { useToast } from 'hooks/useToast';
import {
  MapRef,
  useGlobalGoogleMapPin,
  useMapState,
  useSelectedPolygonId,
} from 'state/map';
import { useMapBoundsState } from 'state/mapSettings';
import { useZoomQuery, useCoordinatesQuery } from 'state/queryParams';
import { trackMapClick } from 'utils/analytics';
import { MapClickClickActions, ProjectGroupTypes } from 'utils/analytics/constants';
import { DEFAULT_MAP_ZOOM, ZOOM_THRESHOLD } from 'utils/constants';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import { getBoundingBoxesFromPolygon } from 'utils/polygons';
import type { IdentifiableCoords } from 'types/map';

export type CustomMapProps = MapProps & {
  selectedMarker?: string;
  points?: IdentifiableCoords[];
  onChangeBounds?(bounds: number[], zoom: number): void;
  loading?: boolean;
  showPolygons?: boolean;
  defaultToSateliteView?: boolean;
  highlightCurrentPolygon?: boolean;
  allowPolygonInteraction?: boolean;
  showLoadingSpinner?: boolean;
  saveMapTypePreference?: boolean;
};

const CustomMap = ({
  center: propCenter,
  points,
  showPolygons = true,
  mapProps,
  defaultToSateliteView = false,
  saveMapTypePreference = false,
  highlightCurrentPolygon = false,
  allowPolygonInteraction = true,
  showLoadingSpinner = true, // Default to true for backwards compatibility
  ...props
}: CustomMapProps) => {
  const [zoomQuery, setZoomQuery] = useZoomQuery();
  const { userPreferredMapType, setUserPreferredMapType } = useUserPreferredMapType();
  const { mapRef, mapLoaded, selectedMapType, setSelectedMapType } = useMapState();
  const [selectedPolygon] = useSelectedPolygonId();
  const [, setBounds] = useState<google.maps.LatLngBounds | undefined>();
  const [, setZoom] = useState(DEFAULT_MAP_ZOOM);
  const [hasFinishedInitialLoad, setHasFinishedInitialLoad] = useState(false);
  const [memoizedMapCenter, setMemoizedMapCenter] =
    useState<MapProps['center']>(propCenter);
  const { googleMapPin } = useGlobalGoogleMapPin();
  const { renderLoadingToast, closeToast, renderToast } = useToast();
  const [toastId, setToastId] = useState('');
  const { coordinatesQuery, coordinatesAsLatLng } = useCoordinatesQuery();
  const [mapCenterState, setMapCenterState] = useState('');
  const { updateBoundsState } = useMapBoundsState();

  useDebouncedSetCenterQuery(mapCenterState);
  useStreetViewVisibility(mapRef as MapRef);

  const {
    data: parcelPolygon,
    isLoading: loadingPolygons,
    isFetching,
    error: parcelPolygonError,
  } = useParcelPolygon();

  const onBoundsChanged = useCallback(() => {
    const map = mapRef;
    if (map) {
      setBounds(map.getBounds() || undefined);
      const mapCenter = map.getCenter()?.toUrlValue();
      if (typeof mapCenter === 'string') {
        setMapCenterState(mapCenter);
      }
    }
  }, [mapRef]);

  const onMapIdle = useCallback(() => {
    if (mapRef && Number(zoomQuery) > ZOOM_THRESHOLD) {
      setBounds(mapRef.getBounds() || undefined);
    }
  }, [mapRef]);

  const showParcelPolygons = useMemo(() => {
    return (
      parcelPolygon &&
      showPolygons &&
      (!loadingPolygons || parcelPolygon.length > 0) &&
      Number(zoomQuery) >= ZOOM_THRESHOLD
    );
  }, [parcelPolygon, showPolygons, loadingPolygons, zoomQuery]);

  const zoomLevel = useMemo(() => {
    // look for zoom prop first, otherwise opt for zoomQuery
    return mapProps?.zoom || Number(zoomQuery) || DEFAULT_MAP_ZOOM;
  }, [mapProps?.zoom, zoomQuery]);

  const onMapTypeIdChanged = useCallback(() => {
    const map = mapRef;
    const mapType = map?.getMapTypeId();
    if (mapType) {
      setSelectedMapType(mapType);
      if (saveMapTypePreference) {
        setUserPreferredMapType(mapType as MapTypeId); // Save the preference
      }
    }
  }, [mapRef, saveMapTypePreference, setSelectedMapType, setUserPreferredMapType]);

  const mapTypeId = useMemo(() => {
    if (saveMapTypePreference && userPreferredMapType) {
      return userPreferredMapType;
    }

    if (defaultToSateliteView) {
      return google.maps.MapTypeId.HYBRID;
    }

    return selectedMapType || google.maps.MapTypeId.ROADMAP;
  }, [
    saveMapTypePreference,
    userPreferredMapType,
    defaultToSateliteView,
    selectedMapType,
  ]);

  const onZoomChanged = useCallback(() => {
    if (mapRef && showPolygons) {
      setZoom(mapRef.getZoom() || DEFAULT_MAP_ZOOM);
      setBounds(mapRef.getBounds() || undefined);
      setZoomQuery(String(mapRef?.getZoom()));
    }
  }, [mapRef, setZoomQuery, zoomQuery]);

  const onTilesLoaded = () => {
    if (!hasFinishedInitialLoad) {
      setHasFinishedInitialLoad(true);

      const map = mapRef;
      map?.setMapTypeId(mapTypeId);
      if (map) {
        updateBoundsState(map.getBounds());
      }

      onZoomChanged();
      if (mapProps?.onTilesLoaded) {
        mapProps?.onTilesLoaded();
      }
    }
  };

  const isWithinPoly = useCallback(
    (polygon: google.maps.Polygon) => {
      if (!memoizedMapCenter || !memoizedMapCenter?.lat || !memoizedMapCenter?.lng) {
        return false;
      }

      const isWithinPolygon = google.maps.geometry.poly.containsLocation(
        memoizedMapCenter,
        polygon,
      );

      return isWithinPolygon;
    },
    [memoizedMapCenter],
  );

  const getPolygonColor = (isSelectedPolygon: boolean, isCurrentPolygon: boolean) => {
    if (isCurrentPolygon || isSelectedPolygon) {
      return Colors.Tangerine700;
    }

    return Colors.ForestGreen900;
  };

  const generatePolygonId = (polygon: { lat: number; lng: number }[]): string => {
    return polygon.map((coord) => `${coord.lat},${coord.lng}`).join('-');
  };

  useEffect(() => {
    // cleanup when we unmount
    return () => {
      // Reset global and local map state
      setZoom(DEFAULT_MAP_ZOOM);
      setBounds(undefined);
    };
  }, []);

  useEffect(() => {
    if (mapRef) {
      const listener = mapRef.addListener('idle', onMapIdle);

      return () => {
        google.maps.event.removeListener(listener);
      };
    }

    // Cleanup function for when mapRef is not defined
    return () => {};
  }, [mapRef, onMapIdle]);

  useEffect(() => {
    if (propCenter && !isEqual(memoizedMapCenter, propCenter)) {
      setMemoizedMapCenter(propCenter);
    }
  }, [propCenter]);

  useEffect(() => {
    let currentToastId = toastId;

    if (highlightCurrentPolygon) {
      return;
    }

    if (isFetching && !currentToastId) {
      // Show the toast if not already shown
      currentToastId = renderLoadingToast({
        toastTitle: 'Loading parcel polygons...',
        status: 'success',
        label: 'Parcel outlines are loading....',
      });
      setToastId(currentToastId);
    } else if (!isFetching && currentToastId) {
      // Close the toast when fetching is done
      closeToast(currentToastId);
      setToastId(''); // Reset the toast ID
      if (parcelPolygonError) {
        renderToast({
          toastTitle: 'failed_to_load_parcel_outlines',
          status: 'error',
          title: 'Error',
          label: 'Parcels failed to load',
        });
      }
    }
  }, [isFetching, toastId, renderLoadingToast, renderToast]);

  return (
    <Map
      {...props}
      showLoadingSpinner={showLoadingSpinner}
      mapProps={{
        zoom: zoomLevel,
        ...mapProps,
        center: memoizedMapCenter,
        onZoomChanged,
        onBoundsChanged,
        onTilesLoaded,
        onMapTypeIdChanged,
        // Tilt value 0 causes the map to always use a 0° overhead view
        // regardless of the zoom level.
        tilt: 0,
        options: {
          minZoom: DEFAULT_MAP_ZOOM,
          mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          scaleControl: true,
          streetViewControl: true,
          streetViewControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM,
          },
          rotateControl: false,
          fullscreenControl: false,
          ...(allowPolygonInteraction ? {} : { mapTypeId }),
        },
      }}
    >
      {mapLoaded && (
        <>
          {showParcelPolygons &&
            parcelPolygon?.map((polygon: any, index: any) => {
              const polygonBounds = getBoundingBoxesFromPolygon(polygon);
              let isCenterInPolygon: boolean = false;
              const polygonId = generatePolygonId(polygon);

              if (highlightCurrentPolygon) {
                isCenterInPolygon = isWithinPoly(
                  new google.maps.Polygon({ paths: polygon }),
                );
              }

              const isPolygonSelected = selectedPolygon === polygonId;

              const polygonColor = getPolygonColor(isPolygonSelected, isCenterInPolygon);

              /* eslint-disable react/no-array-index-key */
              return (
                <Polygon
                  key={`map-polygon-${polygonBounds}-${index}`}
                  paths={polygon}
                  options={{
                    strokeColor: polygonColor,
                    strokeOpacity: 0.9,
                    strokeWeight: 3,
                    fillOpacity: isPolygonSelected ? 0.4 : 0,
                    fillColor: polygonColor,
                    zIndex:
                      isPolygonSelected || isCenterInPolygon
                        ? Number(Z_INDEX_DICT.SELECTED_POLYGON)
                        : undefined,
                  }}
                  onClick={() => {
                    trackMapClick({
                      groupId: '0',
                      groupType: ProjectGroupTypes.UNCATEGORIZED,
                      clickAction: MapClickClickActions.PARCEL_POLYGON,
                    });
                  }}
                />
              );
              /* eslint-enable */
            })}
          {points && points.length === 1 && (
            <Marker position={{ lat: points[0].lat, lng: points[0].lng }} />
          )}
          {googleMapPin.longitude && googleMapPin.latitude && (
            <Marker
              position={{ lat: googleMapPin.latitude, lng: googleMapPin.longitude }}
            />
          )}
          {coordinatesQuery && (
            <Marker
              position={{ lat: coordinatesAsLatLng.lat, lng: coordinatesAsLatLng.lng }}
              clickable={false}
              zIndex={999}
            />
          )}
        </>
      )}
    </Map>
  );
};

export default CustomMap;
